/**
 * `growth/provider` — [`AdProvider`] backends.
 *
 * Two implementations:
 *   - [`NoneAdProvider`] — **DEFAULT**: absolute no-op. `isEnabled` always
 *     `false`, `prepare` always `false`, `show` never displays anything and **never
 *     touches the network or the DOM**. It guarantees that with flags OFF not
 *     a single byte of SDK enters (bundle guard).
 *   - [`WebAdProvider`] — real web backend (AdSense/GPT) with **lazy** script
 *     loading: the `<script>` is injected only in `init()`, and `init()` is only
 *     called if config says ads are alive. Without fill ⇒ `false` and the
 *     UI collapses the slot (never a spinner).
 *
 * The [`makeAdProvider`] factory is the ONLY place that knows concrete
 * implementations — same pattern as `make_provider` from the payments kit: adding a network
 * is one file + one branch, and the placement layer is unaware.
 */

import { AD_NETWORK_NONE, hardenAdInitOptions } from "./config";
import type {
  AdEvent,
  AdEventName,
  AdInitOptions,
  AdKind,
  AdProvider,
  AdsConfig,
  AdShowResult,
} from "./types";

/** Minimal event emitter shared by the backends. */
class AdEmitter {
  private readonly listeners = new Map<AdEventName, Set<(e: AdEvent) => void>>();

  on(ev: AdEventName, cb: (e: AdEvent) => void): () => void {
    const set = this.listeners.get(ev) ?? new Set();
    set.add(cb);
    this.listeners.set(ev, set);
    return () => {
      set.delete(cb);
    };
  }

  emit(ev: AdEventName, payload: AdEvent): void {
    for (const cb of this.listeners.get(ev) ?? []) {
      cb(payload);
    }
  }
}

/**
 * No-op backend. **Portfolio default** and the only implementation bundled in
 * tier C/D apps (B2B and privacy): there is no SDK to load.
 */
export class NoneAdProvider implements AdProvider {
  readonly id = AD_NETWORK_NONE;
  private readonly emitter = new AdEmitter();

  async init(): Promise<void> {
    // Intentionally empty: loading anything here would break the "no ads, no SDK" promise.
  }

  isEnabled(): boolean {
    return false;
  }

  async prepare(): Promise<boolean> {
    return false;
  }

  async show(): Promise<AdShowResult> {
    return { shown: false };
  }

  on(ev: AdEventName, cb: (e: AdEvent) => void): () => void {
    return this.emitter.on(ev, cb);
  }
}

/** How to inject the SDK (injectable for testing without DOM or network). */
export interface AdScriptLoader {
  /** Must resolve to `true` if the SDK is available. */
  load(src: string): Promise<boolean>;
}

/** Default loader: one `<script async>` in the `<head>`, only once. */
export function createDomScriptLoader(): AdScriptLoader {
  const loaded = new Map<string, Promise<boolean>>();
  return {
    load(src: string): Promise<boolean> {
      const existing = loaded.get(src);
      if (existing) {
        return existing;
      }
      const promise = new Promise<boolean>((resolve) => {
        if (typeof document === "undefined") {
          resolve(false);
          return;
        }
        const el = document.createElement("script");
        el.src = src;
        el.async = true;
        el.crossOrigin = "anonymous";
        el.addEventListener("load", () => {
          resolve(true);
        });
        el.addEventListener("error", () => {
          resolve(false);
        });
        document.head.append(el);
      });
      loaded.set(src, promise);
      return promise;
    },
  };
}

/** Formats a WEB network can actually serve (no rewarded ads on web). */
const WEB_SUPPORTED_KINDS: ReadonlySet<AdKind> = new Set(["native", "banner", "interstitial"]);

const ADSENSE_SCRIPT = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js";

/**
 * Real web backend (AdSense by default). The SDK loads in `init()` and NEVER
 * before: until the app says "ads are live", there is no request.
 *
 * `show()` for a web format does not open anything itself—on web, the
 * "display" is done by the slot rendered by `NativeAdCard`/`AdBreakpoint`; this
 * method confirms that the SDK and fill exist and emits the impression.
 */
export class WebAdProvider implements AdProvider {
  readonly id: string;
  private readonly emitter = new AdEmitter();
  private ready = false;
  private initPromise?: Promise<void>;

  constructor(
    private readonly config: AdsConfig,
    private readonly loader: AdScriptLoader = createDomScriptLoader(),
    private readonly scriptUrl: string = ADSENSE_SCRIPT,
  ) {
    this.id = config.network;
  }

  init(opts: AdInitOptions = {}): Promise<void> {
    // Idempotent: several screens may request init at once.
    this.initPromise ??= (async () => {
      if (!this.config.enabled || !this.config.clientId) {
        // Without a flag or configured client, the network is NOT touched.
        return;
      }
      // The caller can only HARDEN: `hardenAdInitOptions` prevents an
      // `init({ childDirected: false, contextualOnly: false })` from undoing the
      // regime that `resolveAdsConfig` already clamped. Policy is not negotiated at
      // the call site.
      const safe = hardenAdInitOptions(this.config, opts);
      const url = new URL(this.scriptUrl);
      url.searchParams.set("client", this.config.clientId);
      if (safe.contextualOnly) {
        // NON-personalized ads. In CashSync this is ALWAYS: no
        // signal derived from user money may target (rule 1 of
        // `monetization/ads-policy`).
        url.searchParams.set("npa", "1");
      }
      if (safe.childDirected) {
        // Regime §6: no behavioral targeting and a G rating.
        url.searchParams.set("tag_for_child_directed_treatment", "1");
        url.searchParams.set("tag_for_under_age_of_consent", "1");
        url.searchParams.set("max_ad_content_rating", safe.maxAdContentRating ?? "G");
      }
      this.ready = await this.loader.load(url.toString());
      if (!this.ready) {
        this.emitter.emit("error", {
          kind: "banner",
          placementId: "init",
          error: "ad script failed to load",
        });
      }
    })();
    return this.initPromise;
  }

  isEnabled(kind: AdKind): boolean {
    return this.config.enabled && this.ready && WEB_SUPPORTED_KINDS.has(kind);
  }

  async prepare(kind: AdKind): Promise<boolean> {
    return this.isEnabled(kind);
  }

  async show(kind: AdKind, placementId: string): Promise<AdShowResult> {
    if (!this.isEnabled(kind)) {
      return { shown: false };
    }
    this.emitter.emit("impression", { kind, placementId });
    return { shown: true };
  }

  on(ev: AdEventName, cb: (e: AdEvent) => void): () => void {
    return this.emitter.on(ev, cb);
  }
}

/**
 * FAKE backend for e2e suites and local demos: there is always fill, and the
 * rewarded ad always completes. It **does not touch the network or DOM**, so the
 * suite is hermetic and deterministic.
 *
 * It activates only with explicit `ADS_NETWORK=fake`—a value no real
 * environment uses. The returned `reward` only CERTIFIES that the user completed
 * the ad; the app's placement registry always decides WHAT is awarded, never
 * the network.
 */
export class FakeAdProvider implements AdProvider {
  readonly id = "fake";
  private readonly emitter = new AdEmitter();

  async init(): Promise<void> {
    // Nothing to initialize: this is precisely what makes it deterministic.
  }

  isEnabled(): boolean {
    return true;
  }

  async prepare(): Promise<boolean> {
    return true;
  }

  async show(kind: AdKind, placementId: string): Promise<AdShowResult> {
    this.emitter.emit("impression", { kind, placementId });
    if (kind !== "rewarded") {
      return { shown: true };
    }
    const reward = { type: "completed", amount: 1 };
    this.emitter.emit("reward", { kind, placementId, reward });
    return { shown: true, reward };
  }

  on(ev: AdEventName, cb: (e: AdEvent) => void): () => void {
    return this.emitter.on(ev, cb);
  }
}

/**
 * Factory: the ONLY place that knows concrete implementations.
 *
 * Any unknown network falls back to `none`—a typo in `ADS_NETWORK` disables
 * ads; it never half-loads something.
 */
export function makeAdProvider(config: AdsConfig, loader?: AdScriptLoader): AdProvider {
  if (!config.enabled) {
    return new NoneAdProvider();
  }
  switch (config.network) {
    case "adsense": {
      return new WebAdProvider(config, loader);
    }
    case "fake": {
      return new FakeAdProvider();
    }
    default: {
      return new NoneAdProvider();
    }
  }
}
